import { Component, EventEmitter, Injector, Input, OnInit, Output } from '@angular/core';
import { BsModalService } from 'ngx-bootstrap/modal';
import { ConsultaDto, ConsultaServiceProxy, PacienteDto, StatusConsultaEnum, TipoConsultaEnum, TipoUsuarioEnum, UpdateConsultaDto, UpdatePacienteDto } from '@shared/service-proxies/service-proxies';
import { EditStatusConsultaComponent } from './edit-status-consulta/edit-status-consulta.component';
import { EditConsultaComponent } from './edit-consulta/edit-consulta.component';
import { ModalVisualizarAnexosComponent } from './modal-visualizar-anexos/modal-visualizar-anexos.component';
import { AppComponentBase } from '@shared/app-component-base';
import { finalize } from 'rxjs/operators';
import { appModuleAnimation } from '@shared/animations/routerTransition';
import { Router } from '@angular/router';
import { RemarcarConsultaComponent } from './remarcar-consulta/remarcar-consulta.component';
import * as moment from '@node_modules/moment-timezone';
import Swal, { SweetAlertResult } from 'sweetalert2';
import { EditPacienteDialogComponent } from '@app/paciente/edit-paciente-dialog/edit-paciente-dialog.component';
import { TipoConsultaItem } from '@shared/model/tipo-consulta-enum.model';
import { AnexarExameComponent } from './anexar-exame/anexar-exame.component';

@Component({
  selector: 'default-consultas',
  templateUrl: './default-consultas.component.html',
  styleUrls: ['./default-consultas.component.scss'],
  animations: [appModuleAnimation()]
})

export class DefaultConsultasComponent extends AppComponentBase implements OnInit {
  public statusCancelada = StatusConsultaEnum._1;
  public statusRealizada = StatusConsultaEnum._3;
  public statusConfirmada = StatusConsultaEnum._0;
  public tipoPaciente = TipoUsuarioEnum._0;
  @Input() consultas: ConsultaDto[] = [];
  @Input() tipoUsuario: TipoUsuarioEnum;
  @Input() pageSize: number;
  @Input() pageNumber: number;
  @Input() totalItems: number;
  @Output() onRefresh = new EventEmitter<any>();
  @Output() onGetDataPage = new EventEmitter<any>();

  constructor(
    injector: Injector,
    private _modalService: BsModalService,
    private readonly _consultaService: ConsultaServiceProxy,
    private readonly _router: Router
  ) {
    super(injector);
  }

  ngOnInit(): void {
    window.addEventListener('storage', this.handleStorageEvent.bind(this));
  }

  private handleStorageEvent(event: StorageEvent) {
    if (event.key === 'consultaFinalizada' && event.newValue) {
      localStorage.removeItem('consultaFinalizada');

      Swal.fire({
        title: "Atenção",
        icon: "warning",
        html: "Deseja finalizar esta consulta?",
        showCloseButton: false,
        showCancelButton: true,
        focusConfirm: false,
        allowOutsideClick: false,
        confirmButtonText: "Sim",
        confirmButtonColor: "#E2A150",
        cancelButtonText: "Não",
        cancelButtonColor: "#6c757d"
      }).then((result: SweetAlertResult) => {
        if (result.isConfirmed) {
          const dados = JSON.parse(event.newValue);
          const consultaId = dados.id;
          this._consultaService
            .alterarStatus(consultaId, StatusConsultaEnum._3)
            .subscribe(() => {
              this.getDataPage(1);
            });
        }
      });
    }
  }

  protected getDataPage(event) {
    this.onGetDataPage.emit(event)
  }

  protected edit(consulta: ConsultaDto): void {
    const editModal = this._modalService.show(
      EditConsultaComponent,
      {
        class: 'modal-md',
        ignoreBackdropClick: true,
        initialState: {
          consulta: UpdateConsultaDto.fromJS(consulta.toJSON()),
          tipoUsuario: this.tipoUsuario
        }
      }
    );

    editModal.content.onSave.subscribe(() => {
      abp.notify.success("Consulta alterada com sucesso!");
      this.onRefresh.emit();
    });
  }

  protected editStatus(consulta: ConsultaDto): void {
    const editStatusModal = this._modalService.show(
      EditStatusConsultaComponent,
      {
        class: 'modal-md',
        ignoreBackdropClick: true,
        initialState: {
          consulta: consulta.clone(),
        }
      }
    );

    editStatusModal.content.onSave.subscribe(() => {
      abp.notify.success("Status da consulta alterado com sucesso!");
      this.onRefresh.emit();
    });
  }

  protected remarcarConsulta(consulta: ConsultaDto): void {
    const editStatusModal = this._modalService.show(
      RemarcarConsultaComponent,
      {
        class: 'modal-md',
        ignoreBackdropClick: true,
        initialState: {
          consulta: consulta.clone(),
        }
      }
    );

    editStatusModal.content.onSave.subscribe(() => {
      abp.notify.success("Consulta remarcarda com sucesso!");
      this.onRefresh.emit();
    });
  }

  protected confirmarConsulta(consulta: ConsultaDto): void {
    abp.message.confirm(`Deseja realmente confirmar sua presença na consulta de ${consulta.descricao}, marcada para o dia ${consulta.dataConsulta.format("DD/MM/YYYY")} às ${consulta.dataConsulta.format("HH:mm")}?`, "Atenção", (option) => {
      if (option) {
        abp.ui.setBusy();
        this._consultaService.alterarStatus(consulta.id, this.statusConfirmada).pipe(
          finalize(() => {
            abp.ui.clearBusy();
          })
        ).subscribe(() => {
          abp.notify.success("Consulta confirmada!");
          this.onRefresh.emit();
        });
      }
    });
  }

  protected solicitarCancelamento(consulta: ConsultaDto): void {
    abp.message.confirm(`Deseja realmente solicitar o cancelmento da consulta de ${consulta.descricao}, marcada para o dia ${consulta.dataConsulta.format("DD/MM/YYYY")} às ${consulta.dataConsulta.format("HH:mm")}?`, "Atenção", (option) => {
      if (option) {
        abp.ui.setBusy();
        this._consultaService.solicitarCancelamento(consulta.id).pipe(
          finalize(() => {
            abp.ui.clearBusy();
          })
        ).subscribe(() => {
          abp.notify.success("Solicitação feita com sucesso!");
          this.onRefresh.emit();
        });
      }
    });
  }

  protected visualizarAnexos(consulta: ConsultaDto): void {
    const anexosModal = this._modalService.show(
      ModalVisualizarAnexosComponent,
      {
        class: 'modal-md',
        ignoreBackdropClick: true,
        initialState: {
          consulta: UpdateConsultaDto.fromJS(consulta.toJSON()),
        }
      }
    );

    anexosModal.content.onSave.subscribe(() => {
      abp.notify.success("Anexos atualizados com sucesso!");
      this.onRefresh.emit();
    });
  }

  protected iniciarConsulta(consulta: ConsultaDto): void {
    const urlAtual = this._router.url;

    let basePath: string;

    if (urlAtual.includes('/app/profissional-saude')) {
      basePath = 'profissional-saude';
    } else if (urlAtual.includes('/app/paciente')) {
      basePath = 'paciente';
    } else {
      abp.notify.error("Não foi possível iniciar a consulta. Caminho inválido.");
      return;
    }

    const url = this._router.serializeUrl(
      this._router.createUrlTree([
        'app',
        basePath,
        'video',
        consulta.id
      ])
    );

    window.open(window.location.origin + url, '_blank');
  }


  protected verificarData(dataConsulta: moment.Moment): boolean {
    return dataConsulta.isSameOrAfter(moment());
  }

  protected finalizarCadastro(paciente: PacienteDto): void {
    const pacienteDto = UpdatePacienteDto.fromJS(paciente);
    const editDialog = this._modalService.show(
      EditPacienteDialogComponent,
      {
        class: 'modal-lg',
        initialState: {
          paciente: pacienteDto,
        },
      }
    );

    editDialog.content.onSave.subscribe(() => {
      this.onRefresh.emit();
    });
  }

  protected anexarExame(consulta: ConsultaDto): void {
    const editDialog = this._modalService.show(
      AnexarExameComponent,
      {
        class: 'modal-lg',
        ignoreBackdropClick: true,
        initialState: {
          consulta: consulta,
        },
      }
    );

    editDialog.content.onSave.subscribe(() => {
      this.onRefresh.emit();
    });
  }

  protected showTelecosultaOption(consulta: ConsultaDto): boolean {
    return consulta.tipoConsultaString == "Teleconsulta" && moment(consulta.dataConsulta).isSameOrAfter(moment())
  }
}
